import AccountBoxOutlined from '@mui/icons-material/AccountBoxOutlined';
import LockOutlined from '@mui/icons-material/LockOutlined';
import MobileFriendlyOutlined from '@mui/icons-material/MobileFriendlyOutlined';
import PersonPinOutlined from '@mui/icons-material/PersonPinOutlined';
import SyncOutlined from '@mui/icons-material/SyncOutlined';
import { Box, Button, Divider, Stack, Typography, useTheme } from '@mui/material';
import { Provider } from 'graphql/generated/graphqlTypes';
import React, { useState } from 'react';
import EntraID from 'v2/assets/vector/EntraId';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import { useWizardStepContext } from 'v2/components/WizardModal';
import useChooseProvider from 'v2/hooks/onboarding/useChooseProvider/useChooseProvider';

const STEP_INDEX = 2;

export interface Props {}

const ChooseProviderStep: React.FC<React.PropsWithChildren<Props>> = () => {
  const theme = useTheme();
  const { showSnackbar } = useSnackbarContext();
  const handleFetchError = (errorMessage: string) => showSnackbar(errorMessage, { severity: 'error' });
  const { chooseProvider, loading } = useChooseProvider({
    handleFetchError
  });
  const { setFooterContent, steps } = useWizardStepContext();
  const [chosenProvider, setChosenProvider] = useState<Provider>(Provider.Cognito);

  const onNextButtonClick = () => {
    if (!loading) chooseProvider(chosenProvider);
  };

  React.useLayoutEffect(() => {
    setFooterContent(
      STEP_INDEX,
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', md: 'row' }, gap: 2 }}>
        <Button
          key="device-group-name-step-submit"
          id="choose-provider-continue-button"
          variant="contained"
          type="submit"
          loading={loading}
          onClick={() => onNextButtonClick()}
        >
          Continue
        </Button>
      </Box>
    );
  }, [setFooterContent, steps, loading, chosenProvider]);

  return (
    <Stack flexDirection="column" gap={3}>
      <Stack>
        <Typography variant="h4" color="textPrimary">
          Create ATLAS administrator account
        </Typography>
        <Typography variant="body1" color="textPrimary">
          Set up your admin access by creating an account or connecting an identity provider for seamless login.
        </Typography>
      </Stack>
      <Divider />
      <Stack flexDirection="row" id="content" gap={2}>
        <Stack flexDirection="column" gap={1} height="100%">
          <Stack
            id="cognito-radio"
            onClick={() => setChosenProvider(Provider.Cognito)}
            flexDirection="row"
            gap={0.5}
            padding="0 32px 0 16px"
            width="346px"
            height="80px"
            alignItems="center"
            style={
              chosenProvider === 'COGNITO'
                ? {
                    borderRadius: '8px',
                    border: `1px solid ${theme.palette.primary.main}`,
                    backgroundColor: theme.custom.status.primary.selected,
                    cursor: 'pointer'
                  }
                : {
                    borderRadius: '8px',
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.custom.transparent,
                    cursor: 'pointer'
                  }
            }
          >
            <AccountBoxOutlined
              style={{ height: '36px', width: '36px', margin: '6px', color: theme.palette.text.primary }}
            />
            <Typography variant="body1" color="textPrimary">
              Create administrator account
            </Typography>
          </Stack>
          <Stack flexDirection="row" width="100%" justifyContent="center" alignItems="center" gap={1}>
            <Divider orientation="horizontal" style={{ height: '1px', width: '40%' }} />
            <Typography color="textPrimary">or</Typography>
            <Divider orientation="horizontal" style={{ height: '1px', width: '40%' }} />
          </Stack>
          <Stack
            id="entra-radio"
            onClick={() => setChosenProvider(Provider.Entra)}
            flexDirection="row"
            gap={0.5}
            padding="0 16px"
            height="80px"
            alignItems="center"
            style={
              chosenProvider === 'ENTRA'
                ? {
                    borderRadius: '8px',
                    border: `1px solid ${theme.palette.primary.main}`,
                    backgroundColor: theme.custom.status.primary.selected,
                    cursor: 'pointer'
                  }
                : {
                    borderRadius: '8px',
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.custom.transparent,
                    cursor: 'pointer'
                  }
            }
          >
            <EntraID name="EntraId" fontSize="large" sx={{ color: theme.palette.text.primary }} />
            <Typography variant="body1" color="textPrimary">
              Connect Entra ID
            </Typography>
          </Stack>
        </Stack>
        {chosenProvider === 'COGNITO' ? (
          <Stack flexDirection="column" gap={2}>
            <Stack gap={1} alignItems="center" direction="row">
              <AccountBoxOutlined
                style={{ height: '36px', width: '36px', margin: '6px', color: theme.palette.text.primary }}
              />
              <Typography variant="h5" color="textPrimary">
                Create administrator account
              </Typography>
            </Stack>
            <Stack gap={1} margin="8px" direction="row">
              <LockOutlined style={{ color: theme.palette.text.primary }} />
              <Stack flexDirection="column">
                <Typography variant="subtitle1" color="textPrimary">
                  Email address &amp; password login
                </Typography>
                <Typography variant="body2" color="textPrimary">
                  Administrator(s) can login with their email address and a personal password.
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        ) : (
          <Stack flexDirection="column" gap={3}>
            <Stack flexDirection="column">
              <Stack direction="row" gap="14px">
                <EntraID name="EntraId" fontSize="large" />
                <Typography variant="h5" color="textPrimary">
                  Microsoft Entra ID
                </Typography>
              </Stack>
              <Typography variant="body2" fontStyle={'italic'} color="textPrimary">
                Connect EntraID to ATLAS, and benefit from the following features:
              </Typography>
            </Stack>
            <Stack alignItems="center" direction="row" gap={1} margin="8px">
              <LockOutlined style={{ color: theme.palette.text.primary }} />
              <Stack flexDirection="column" alignSelf="flex-start">
                <Typography variant="subtitle1" color="textPrimary">
                  Single Sign On
                </Typography>
                <Typography variant="body2" color="textPrimary">
                  Use your Microsoft credentials to log into ATLAS.
                </Typography>
              </Stack>
            </Stack>
            <Stack alignItems="center" gap={1} margin="8px" direction="row">
              <SyncOutlined style={{ color: theme.palette.text.primary }} />
              <Stack flexDirection="column" alignSelf="flex-start">
                <Typography variant="subtitle1" color="textPrimary">
                  Automated user management
                </Typography>
                <Typography variant="body2" color="textPrimary">
                  Synchronize your Microsoft Entra ID user groups with ATLAS.
                </Typography>
              </Stack>
            </Stack>
            <Stack alignItems="center" gap={1} margin="8px" direction="row">
              <MobileFriendlyOutlined style={{ color: theme.palette.text.primary }} />
              <Stack flexDirection="column" alignSelf="flex-start">
                <Typography variant="subtitle1" color="textPrimary">
                  Decentralized digital key management
                </Typography>
                <Typography variant="body2" color="textPrimary">
                  Synchronized users can manage their own mobile device in the ATLAS Portal.
                </Typography>
              </Stack>
            </Stack>
            <Stack alignItems="center" gap={1} margin="8px" direction="row">
              <PersonPinOutlined style={{ color: theme.palette.text.primary }} />
              <Stack flexDirection="column" alignSelf="flex-start">
                <Typography variant="subtitle1" color="textPrimary">
                  Improved visitor management
                </Typography>
                <Typography variant="body2" color="textPrimary">
                  Seamlessly host visitors with integrated Outlook scheduling and mobile access.
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export default ChooseProviderStep;
